import { useEffect, useId, useRef, useState } from "react";
import { addMonths, monthGrid, monthTitle, shortUsDate } from "@/calendar";
import { Icon } from "@/components/Icon";
import { todayIso } from "@/format";

const Weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/**
 * A date field whose calendar shows which days have something on them, in bold: here, the check
 * dates reports were paid with. The browser's own date picker can't mark days, so this draws its
 * own month view.
 */
export function CheckDatePicker({
  value,
  onChange,
  marked,
  label,
}: {
  value: string;
  onChange: (iso: string) => void;
  marked: ReadonlySet<string>;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(value || todayIso());
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  const today = todayIso();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  function choose(iso: string) {
    onChange(iso);
    setOpen(false);
  }

  return (
    <div className="date-picker" ref={box}>
      <button
        type="button"
        className="date-picker-field"
        aria-label={`${label}: ${value ? shortUsDate(value) : "none"}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          if (!open) setMonth(value || today);
          setOpen(!open);
        }}
      >
        <span>{value ? shortUsDate(value) : "Choose a date"}</span>
        <Icon name="calendar" size={18} />
      </button>
      {open && (
        <div id={id} className="date-picker-pop" role="dialog" aria-label={label}>
          <div className="date-picker-head">
            <strong>{monthTitle(month)}</strong>
            <button
              type="button"
              className="icon-btn"
              aria-label="Previous month"
              onClick={() => setMonth(addMonths(month, -1))}
            >
              <Icon name="chevronLeft" size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Next month"
              onClick={() => setMonth(addMonths(month, 1))}
            >
              <Icon name="chevronRight" size={18} />
            </button>
          </div>
          <div className="date-picker-grid">
            {Weekdays.map((day) => (
              <span key={day} className="date-picker-weekday">
                {day}
              </span>
            ))}
            {monthGrid(month).map((day) => {
              const classes = [
                "date-picker-day",
                day.inMonth ? "" : "outside",
                marked.has(day.iso) ? "marked" : "",
                day.iso === value ? "chosen" : "",
                day.iso === today ? "today" : "",
              ].join(" ");
              return (
                <button
                  key={day.iso}
                  type="button"
                  className={classes}
                  aria-pressed={day.iso === value}
                  aria-label={`${shortUsDate(day.iso)}${marked.has(day.iso) ? ", has a check" : ""}`}
                  onClick={() => choose(day.iso)}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
          <div className="date-picker-foot">
            <span className="muted small">
              <strong>Bold</strong> days have a check
            </span>
            <button type="button" className="link-btn" onClick={() => choose(today)}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
